import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, tap } from "rxjs";
import { appConfig } from "../../shelter.config";
import { ShelterModel } from "../../../../petszel-shared/src/lib/shared-models/shelter.model";
import { ShelterLogoModel } from "../models/shelter-logo.model";
import { ShelterUserModel } from "../models/shelter-user.model";
import {
  IntegrationCredentialResponseModel
} from "../../../../petszel-staff/src/app/models/credential-validation-response.model";
import { ShelterSiteModel } from "../../../../petszel-shared/src/lib/shared-models/shelter-site.model";
import { FeatureFlagModel } from "../models/feature-flags-model";
import { Store } from "@ngrx/store";
import { ShelterAppStateModel } from "../store/ShelterAppStateModel";
import { setShelterSites } from "../store/shelter/sites/shelter-sites.actions";

export interface LatestToSShelterModel {
  id: string;
  version: number;
}

@Injectable({
  providedIn: 'root'
})
export class ShelterService {
  private shelterNameSource = new BehaviorSubject<string>('');
  private shelterSitesSignal: WritableSignal<ShelterSiteModel[]> = signal<ShelterSiteModel[]>([]);
  private shelter$: BehaviorSubject<ShelterModel | null> = new BehaviorSubject<ShelterModel | null>(null);
  private defaultLogo$: BehaviorSubject<ShelterLogoModel | null> = new BehaviorSubject<ShelterLogoModel | null>(null);
  private shelterLogos$: BehaviorSubject<ShelterLogoModel[] | null> = new BehaviorSubject<ShelterLogoModel[] | null>(null);
  private token = localStorage.getItem('token');
  private headers = new HttpHeaders().set('Authorization', `Bearer ${this.token}`);

  shelterName$ = this.shelterNameSource.asObservable();
  shelterObs$: Observable<ShelterModel | null>;
  defaultLogoObs$: Observable<ShelterLogoModel | null>;
  shelterLogosObs$: Observable<ShelterLogoModel[] | null>;
  shelterSites: Signal<ShelterSiteModel[]> = this.shelterSitesSignal as Signal<ShelterSiteModel[]>;

  constructor(private http: HttpClient,
              private store: Store<ShelterAppStateModel>) {
    this.shelterObs$ = this.shelter$.asObservable();
    this.defaultLogoObs$ = this.defaultLogo$.asObservable();
    this.shelterLogosObs$ = this.shelterLogos$.asObservable();
  }

  getSelectedLogo(logoId: string) {
    this.getShelterLogos().subscribe({
      next: logos => {
        this.shelterLogos$.next(logos);
        let selectedLogo = logos?.find(logo => logo.logoId === logoId);
        if (!selectedLogo && logos && logos.length > 0) {
          selectedLogo = logos[0];
        }

        this.defaultLogo$.next(selectedLogo || null);
      },
      error: err => {
        this.shelterLogos$.next([]);
        console.error('Error retrieving shelter logos:', err);
      }
    });
  }

  setShelterManually(shelter: ShelterModel): void {
    this.shelter$.next(shelter);
  }

  getShelter(): Observable<ShelterModel> {
    const url = `${appConfig.apiUrl}Shelter`;
    return this.http.get<ShelterModel>(url, { headers: this.headers }).pipe(
      tap(shelter => {
        shelter.extractionEnabled = shelter.extractionEnabled == null ? false : shelter.extractionEnabled;
        shelter.communicationsEnabled = shelter.communicationsEnabled == null ? false : shelter.communicationsEnabled;

        this.getSelectedLogo(shelter.configuration.defaultLogoId);

        // Automatically update new info in Observable
        this.shelter$.next(shelter);
        this.setShelterName(shelter.businessName); // Update shelter name

        // Pull Shelter Sites
        this.getShelterSites();
      })
    );
  }

  clearShelterName(): void {
    this.shelterNameSource.next('');
  }

  setShelterName(name: string): void {
    this.shelterNameSource.next(name);
  }

  getShelterLogos(): Observable<ShelterLogoModel[]> {
    const url = `${appConfig.apiUrl}Shelter/Logos`
    return this.http.get<ShelterLogoModel[]>(url, { headers: this.headers });
  }

  updateShelter(data: ShelterModel): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  uploadLogo(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/Logos`;
    return this.http.post<any>(url, data, { headers: this.headers });
  }

  deleteLogo(logoId: string): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/Logos?logoId=${logoId}`;
    return this.http.delete(url, { headers: this.headers, responseType: 'text' });
  }

  getUsers(): Observable<ShelterUserModel[]> {
    const url = `${appConfig.apiUrl}Shelter/Users`;
    console.log(url, appConfig)
    return this.http.get<ShelterUserModel[]>(url, { headers: this.headers });
  }

  changePassword(data: { existingPassword: string, newPassword: string }): Observable<any> {
    const url = `${appConfig.apiUrl}Authorize/ChangePassword`;
    return this.http.post(url, data,{ headers: this.headers });
  }

  getLatestToS(): Observable<LatestToSShelterModel> {
    const url = `${appConfig.apiUrl}Shelter/TermsOfService/Latest/Shelter`;
    return this.http.get<LatestToSShelterModel>(url,{ headers: this.headers });
  }

  validateCredentialsShelterLuv(data: any): Observable<IntegrationCredentialResponseModel> {
    const url = `${appConfig.apiUrl}Integrations/Validate/ShelterLuv`;
    return this.http.post<IntegrationCredentialResponseModel>(url, data, { headers: this.headers });
  }

  validateCredentialsShelterBuddy(data: any): Observable<IntegrationCredentialResponseModel> {
    const url = `${appConfig.apiUrl}Integrations/Validate/ShelterBuddy`;
    return this.http.post<IntegrationCredentialResponseModel>(url, data, { headers: this.headers });
  }

  validateCredentialsPPv2(data:any): Observable<IntegrationCredentialResponseModel> {
    const url = `${appConfig.apiUrl}Integrations/Validate/PPv2`;
    return this.http.post<IntegrationCredentialResponseModel>(url, data, { headers: this.headers });
  }

  validateCredentialsShelterLuvPlus(data: any): Observable<IntegrationCredentialResponseModel> {
    const url = `${appConfig.apiUrl}Integrations/Validate/ShelterLuvPlus`;
    return this.http.post<IntegrationCredentialResponseModel>(url, data, { headers: this.headers });
  }

  putShelterBrandAssociations(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/ShelterIdBrandAssociationsInfo`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  putShelterIntegrationInfo(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/IntegrationInfo`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  putWebsiteSocialMediaInfo(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/WebsiteSocialMediaInfo`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  putPartnersInfo(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/PartnersInfo`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  putAcceptTerms(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/AcceptedTermsInfo`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  putShelterDocLinks(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/LinksInfo`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  putShelterConfigurationInfo(data: any): Observable<any> {
    const url = `${appConfig.apiUrl}Shelter/ConfigurationInfo`;
    return this.http.put(url, data, { headers: this.headers, responseType: 'text' });
  }

  /**
   * Pulling Shelter Sites and transform them into a signal
   * To get this value on other components use shelterSites
   * */
  private getShelterSites(): void {
    const url = `${appConfig.apiUrl}Shelter/Sites`;
    this.http.get<ShelterSiteModel[]>(url, { headers: this.headers }).subscribe((sites) => {
      this.shelterSitesSignal.set(sites);
      this.store.dispatch(setShelterSites({ sites }));
    });
  }

  getRoleScopes(): Observable<any[]> {
    const url = `${appConfig.apiUrl}Shelter/ShelterRoleScopes`;
    return this.http.get<any[]>(url, { headers: this.headers });
  }

  getFeatureFlagAdopterHub(): Observable<FeatureFlagModel> {
    const url = `${appConfig.apiUrl}FeatureFlags/AdopterHub`;
    return this.http.get<FeatureFlagModel>(url, { headers: this.headers });
  }
}
